import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Team {
  id: string
  name: string
  description: string
  created_by: string
  created_at: string
  member_count: number
  shared_resources_count?: number
  members: TeamMember[]
  settings: {
    default_visibility: string
    allow_guest_access: boolean
    require_approval: boolean
  }
}

interface TeamMember {
  user_id: string
  email: string
  name: string
  role: string
  joined_at: string
}

interface SharedResource {
  id: string
  team_id: string
  type: string
  name: string
  resource_id: string
  shared_by: string
  shared_at: string
  permissions: string[]
}

interface Activity {
  id: string
  team_id: string
  user_id: string
  user_name: string
  action: string
  details: Record<string, any>
  timestamp: string
}

interface Invitation {
  id: string
  team_id: string
  email: string
  role: string
  status: string
  created_at: string
  expires_at: string
}

const TeamManagementPage: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [resources, setResources] = useState<SharedResource[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'resources' | 'activity' | 'settings'>('overview')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  // Form states
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDescription, setNewTeamDescription] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('viewer')
  const [shareType, setShareType] = useState('report')
  const [shareName, setShareName] = useState('')
  const [shareId, setShareId] = useState('')

  const API_BASE = 'http://192.168.0.98:8090/api'

  useEffect(() => {
    fetchTeams()
  }, [])

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamDetails(selectedTeam.id)
    }
  }, [selectedTeam?.id])

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_BASE}/teams`)
      if (response.ok) {
        const data = await response.json()
        setTeams(data)
        if (data.length > 0 && !selectedTeam) {
          setSelectedTeam(data[0])
        }
      }
    } catch (error) {
      console.error('Error fetching teams:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTeamDetails = async (teamId: string) => {
    try {
      const [membersRes, resourcesRes, activityRes, invitationsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/teams/${teamId}/members`),
        fetch(`${API_BASE}/teams/${teamId}/resources`),
        fetch(`${API_BASE}/teams/${teamId}/activity`),
        fetch(`${API_BASE}/teams/${teamId}/invitations`),
        fetch(`${API_BASE}/teams/${teamId}/statistics`)
      ])

      if (membersRes.ok) setMembers(await membersRes.json())
      if (resourcesRes.ok) setResources(await resourcesRes.json())
      if (activityRes.ok) setActivities(await activityRes.json())
      if (invitationsRes.ok) setInvitations(await invitationsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching team details:', error)
    }
  }

  const handleCreateTeam = async () => {
    try {
      const response = await fetch(`${API_BASE}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTeamName,
          description: newTeamDescription,
          created_by: 'user-1',
          creator_name: 'Admin User',
          creator_email: 'admin@expandly.com'
        })
      })

      if (response.ok) {
        const newTeam = await response.json()
        setTeams([newTeam, ...teams])
        setSelectedTeam(newTeam)
        setShowCreateModal(false)
        setNewTeamName('')
        setNewTeamDescription('')
      }
    } catch (error) {
      console.error('Error creating team:', error)
    }
  }

  const handleInviteMember = async () => {
    if (!selectedTeam) return

    try {
      const response = await fetch(`${API_BASE}/teams/${selectedTeam.id}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          invited_by: 'user-1',
          invited_by_name: 'Admin User'
        })
      })

      if (response.ok) {
        fetchTeamDetails(selectedTeam.id)
        setShowInviteModal(false)
        setInviteEmail('')
        setInviteRole('viewer')
      }
    } catch (error) {
      console.error('Error inviting member:', error)
    }
  }

  const handleShareResource = async () => {
    if (!selectedTeam) return

    try {
      const response = await fetch(`${API_BASE}/teams/${selectedTeam.id}/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource_type: shareType,
          resource_id: shareId,
          name: shareName,
          shared_by: 'user-1',
          shared_by_name: 'Admin User',
          permissions: ['view', 'export']
        })
      })

      if (response.ok) {
        fetchTeamDetails(selectedTeam.id)
        setShowShareModal(false)
        setShareName('')
        setShareId('')
      }
    } catch (error) {
      console.error('Error sharing resource:', error)
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeam || !confirm('Remove this member from the team?')) return

    try {
      const response = await fetch(`${API_BASE}/teams/${selectedTeam.id}/members/${userId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchTeamDetails(selectedTeam.id)
      }
    } catch (error) {
      console.error('Error removing member:', error)
    }
  }

  const handleUpdateRole = async (userId: string, newRole: string) => {
    if (!selectedTeam) return

    try {
      const response = await fetch(`${API_BASE}/teams/${selectedTeam.id}/members/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      })

      if (response.ok) {
        fetchTeamDetails(selectedTeam.id)
      }
    } catch (error) {
      console.error('Error updating role:', error)
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800'
      case 'admin': return 'bg-blue-100 text-blue-800'
      case 'editor': return 'bg-green-100 text-green-800'
      case 'viewer': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'report': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
      case 'dashboard': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      )
      case 'search': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
      default: return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    }
  }

  const getActivityDescription = (activity: Activity) => {
    switch (activity.action) {
      case 'created_team':
        return `created team "${activity.details.team_name}"`
      case 'joined_team':
        return `joined as ${activity.details.role}`
      case 'left_team':
        return 'left the team'
      case 'shared_resource':
        return `shared ${activity.details.resource_type} "${activity.details.resource_name}"`
      case 'role_changed':
        return `role changed from ${activity.details.old_role} to ${activity.details.new_role}`
      case 'sent_invitation':
        return `invited ${activity.details.email} as ${activity.details.role}`
      case 'generated_report':
        return `generated report "${activity.details.report_name}"`
      case 'commented':
        return `commented on "${activity.details.resource_name}"`
      default:
        return activity.action.replace(/_/g, ' ')
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60))
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60))
        return `${minutes}m ago`
      }
      return `${hours}h ago`
    }
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </main>
    </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
            <p className="text-gray-600 mt-1">Manage teams, members, and collaborate on resources</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Team
          </button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Team List Sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Your Teams</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {teams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => setSelectedTeam(team)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedTeam?.id === team.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <h4 className="font-medium text-gray-900">{team.name}</h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{team.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      {team.member_count} members
                    </div>
                  </button>
                ))}

                {teams.length === 0 && (
                  <div className="p-4 text-center text-gray-500">
                    No teams yet. Create one to get started.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Team Details */}
          <div className="col-span-9">
            {selectedTeam ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Team Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedTeam.name}</h2>
                      <p className="text-gray-600 mt-1">{selectedTeam.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                      >
                        Invite Member
                      </button>
                      <button
                        onClick={() => setShowShareModal(true)}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      >
                        Share Resource
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    {(['overview', 'members', 'resources', 'activity', 'settings'] as const).map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-3 text-sm font-medium border-b-2 ${
                          activeTab === tab
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'overview' && statistics && (
                    <div className="space-y-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-700">{statistics.member_count}</div>
                          <div className="text-sm text-blue-600">Total Members</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-green-700">{statistics.active_members}</div>
                          <div className="text-sm text-green-600">Active (7 days)</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-purple-700">{statistics.shared_resources_count}</div>
                          <div className="text-sm text-purple-600">Shared Resources</div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-orange-700">{statistics.activity_count}</div>
                          <div className="text-sm text-orange-600">Total Activities</div>
                        </div>
                      </div>

                      {/* Role Distribution */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Role Distribution</h3>
                        <div className="flex gap-4">
                          {Object.entries(statistics.role_distribution || {}).map(([role, count]) => (
                            <div key={role} className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(role)}`}>
                                {role}
                              </span>
                              <span className="text-gray-600">{count as number}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Resources by Type */}
                      {statistics.resources_by_type && Object.keys(statistics.resources_by_type).length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">Resources by Type</h3>
                          <div className="flex gap-4">
                            {Object.entries(statistics.resources_by_type).map(([type, count]) => (
                              <div key={type} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                                <span className="text-gray-600">{getResourceIcon(type)}</span>
                                <span className="font-medium capitalize">{type}</span>
                                <span className="text-gray-500">({count as number})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recent Activity */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Activity</h3>
                        <div className="space-y-3">
                          {activities.slice(0, 5).map(activity => (
                            <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                                {activity.user_name.charAt(0)}
                              </div>
                              <div className="flex-1">
                                <p className="text-gray-900">
                                  <span className="font-medium">{activity.user_name}</span>{' '}
                                  {getActivityDescription(activity)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{formatDate(activity.timestamp)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'members' && (
                    <div className="space-y-4">
                      {/* Pending Invitations */}
                      {invitations.length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">Pending Invitations</h3>
                          <div className="space-y-2">
                            {invitations.map(inv => (
                              <div key={inv.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div>
                                  <span className="font-medium">{inv.email}</span>
                                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${getRoleBadgeColor(inv.role)}`}>
                                    {inv.role}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-500">
                                  Expires {formatDate(inv.expires_at)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Members List */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Team Members ({members.length})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {members.map(member => (
                              <tr key={member.user_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                                      {member.name.charAt(0)}
                                    </div>
                                    <div>
                                      <div className="font-medium text-gray-900">{member.name}</div>
                                      <div className="text-sm text-gray-500">{member.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {member.role === 'owner' ? (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                                      {member.role}
                                    </span>
                                  ) : (
                                    <select
                                      value={member.role}
                                      onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                                      className="px-2 py-1 border rounded text-sm"
                                    >
                                      <option value="admin">Admin</option>
                                      <option value="editor">Editor</option>
                                      <option value="viewer">Viewer</option>
                                    </select>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {formatDate(member.joined_at)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {member.role !== 'owner' && (
                                    <button
                                      onClick={() => handleRemoveMember(member.user_id)}
                                      className="text-red-600 hover:text-red-700 text-sm"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === 'resources' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Shared Resources ({resources.length})</h3>
                      {resources.length > 0 ? (
                        <div className="grid gap-4">
                          {resources.map(resource => (
                            <div key={resource.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                              <div className="flex items-center gap-4">
                                <div className={`p-2 rounded-lg ${
                                  resource.type === 'report' ? 'bg-blue-100 text-blue-600' :
                                  resource.type === 'dashboard' ? 'bg-purple-100 text-purple-600' :
                                  'bg-green-100 text-green-600'
                                }`}>
                                  {getResourceIcon(resource.type)}
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">{resource.name}</h4>
                                  <p className="text-sm text-gray-500">
                                    Shared by {resource.shared_by} • {formatDate(resource.shared_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {resource.permissions.map(perm => (
                                  <span key={perm} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                    {perm}
                                  </span>
                                ))}
                                <button className="ml-2 text-blue-600 hover:text-blue-700 text-sm">
                                  View
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No resources shared yet. Click "Share Resource" to get started.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'activity' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Activity Feed</h3>
                      <div className="space-y-3">
                        {activities.map(activity => (
                          <div key={activity.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                              {activity.user_name.charAt(0)}
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-900">
                                <span className="font-medium">{activity.user_name}</span>{' '}
                                {getActivityDescription(activity)}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">{formatDate(activity.timestamp)}</p>
                            </div>
                          </div>
                        ))}

                        {activities.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            No activity recorded yet.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'settings' && selectedTeam && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Team Settings</h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Default Visibility</label>
                          <select className="w-full border rounded-lg px-3 py-2">
                            <option value="team" selected={selectedTeam.settings.default_visibility === 'team'}>Team Only</option>
                            <option value="private" selected={selectedTeam.settings.default_visibility === 'private'}>Private</option>
                            <option value="public" selected={selectedTeam.settings.default_visibility === 'public'}>Public</option>
                          </select>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">Allow Guest Access</div>
                            <div className="text-sm text-gray-500">Allow non-members to view shared resources</div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked={selectedTeam.settings.allow_guest_access} />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                          </label>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">Require Approval</div>
                            <div className="text-sm text-gray-500">New members must be approved by admins</div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked={selectedTeam.settings.require_approval} />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                          </label>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-gray-200">
                        <h4 className="text-red-600 font-medium mb-2">Danger Zone</h4>
                        <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                          Delete Team
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Team Selected</h3>
                <p className="text-gray-500 mb-4">Select a team from the sidebar or create a new one</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Your First Team
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Create Team Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Team</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="e.g., European Expansion Team"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTeamDescription}
                    onChange={(e) => setNewTeamDescription(e.target.value)}
                    placeholder="What is this team focused on?"
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTeam}
                  disabled={!newTeamName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Create Team
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Member Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Invite Team Member</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="admin">Admin - Full management access</option>
                    <option value="editor">Editor - Can edit and share</option>
                    <option value="viewer">Viewer - Read-only access</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteMember}
                  disabled={!inviteEmail.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Resource Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Share Resource</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                  <select
                    value={shareType}
                    onChange={(e) => setShareType(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="report">Report</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="search">Saved Search</option>
                    <option value="query">Query</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resource Name</label>
                  <input
                    type="text"
                    value={shareName}
                    onChange={(e) => setShareName(e.target.value)}
                    placeholder="e.g., Q4 Market Analysis"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resource ID</label>
                  <input
                    type="text"
                    value={shareId}
                    onChange={(e) => setShareId(e.target.value)}
                    placeholder="e.g., report-123"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareResource}
                  disabled={!shareName.trim() || !shareId.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Share Resource
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
    </div>
  )
}

export default TeamManagementPage
